import Link from 'next/link';
import { Calendar } from 'react-big-calendar';

import { css, styled } from '@lichtblick/styled';
import { Colors, Spacing } from '@lichtblick/theme';

import { Text } from '..';

export const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

/** Hides children on viewports ≤768px using CSS only — no JS/hydration flash. */
export const HideOnMobile = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: none;
  }
`;

/** Only visible on viewports ≤768px — hidden on desktop via CSS. */
export const MobileOnly = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

/** Only visible on viewports >768px — hidden on mobile via CSS. */
export const DesktopOnly = styled.div`
  display: block;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const CustomCalendar = styled(Calendar)`
  margin-bottom: ${Spacing['3Xl']};
  min-height: 100vh;

  .rbc-month-header {
    background-color: white;
    position: sticky;
    top: 0;
    z-index: var(--z-index-panel-overlay);
  }
  .rbc-month-row {
    overflow: visible;
    .rbc-row-bg {
      min-height: inherit;
    }
  }
`;

export const Container = styled.div<{ $isBinding?: boolean; $variant: string }>`
  align-content: center;
  cursor: default;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.Xxs};
  justify-content: center;
  min-height: 5rem;
  padding: ${Spacing.Xs} ${Spacing.Xxs} ${Spacing.Xxs};
  position: relative;
  width: inherit;

  &:hover .calendar-drag-handle::after {
    opacity: 1;
  }
  ${({ $isBinding, $variant }) => {
    switch ($variant) {
      case 'Vor-Ort-Termin':
        return css`
          --appointment-accent: ${Colors.blue['100']};
          --appointment-pill-bg: ${Colors.blue['20']};
          background-color: ${Colors.blue[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.blue['100']}`};
        `;
      case 'Online-Begehung':
        return css`
          --appointment-accent: ${Colors.skyBlue['100']};
          --appointment-pill-bg: ${Colors.skyBlue['20']};
          background-color: ${Colors.skyBlue[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.skyBlue['100']}`};
        `;
      case 'Warenlieferung':
        return css`
          --appointment-accent: ${Colors.violet['100']};
          --appointment-pill-bg: ${Colors.violet['20']};
          background-color: ${Colors.violet[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.violet['100']}`};
        `;
      case 'DC-Montagetermin':
      case 'DC-Nachbesserungstermin (InSe-verschuldet)':
      case 'DC-Nachbesserungstermin (Kunde-verschuldet)':
        return css`
          --appointment-accent: ${Colors.red['100']};
          --appointment-pill-bg: ${Colors.red['10']};
          background-color: ${Colors.red[$isBinding ? '50' : '10']};
          border-left: ${`3px solid ${Colors.red['100']}`};
        `;
      case 'AC Installation':
      case 'AC-Montagetermin':
        return css`
          --appointment-accent: ${Colors.yellow['100']};
          --appointment-pill-bg: ${Colors.yellow['20']};
          background-color: ${Colors.yellow[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.yellow['100']}`};
        `;
      case 'Gerüstaufbau':
      case 'Gerüstabbau':
        return css`
          --appointment-accent: ${Colors.turquoise['100']};
          --appointment-pill-bg: ${Colors.turquoise['20']};
          background-color: ${Colors.turquoise[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.turquoise['100']}`};
        `;
      case 'Zählersetzungstermin':
        return css`
          --appointment-accent: ${Colors.green['100']};
          --appointment-pill-bg: ${Colors.green['20']};
          background-color: ${Colors.green[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.green['100']}`};
        `;
      default:
        return css`
          --appointment-accent: ${Colors.gray['100']};
          --appointment-pill-bg: ${Colors.gray['20']};
          background-color: ${Colors.gray[$isBinding ? '50' : '20']};
          border-left: ${`3px solid ${Colors.gray['100']}`};
        `;
    }
  }}
`;

export const ItemWrapper = styled.div`
  align-items: center;
  color: black;
  display: flex;
  gap: ${Spacing.Xxs};
  min-width: 0;
  width: 100%;
`;

export const DragHandleBar = styled.div`
  cursor: grab;
  height: 1rem;
  left: 0;
  overflow: visible;
  position: absolute;
  right: 0;
  top: 0;
  z-index: var(--z-index-raised2);

  &::after {
    background-color: var(--appointment-accent, ${Colors.gray['100']});
    border-radius: var(--border-radius-xs);
    content: '';
    height: ${Spacing['3Xs']};
    left: ${Spacing.Xxs};
    opacity: 0;
    position: absolute;
    right: ${Spacing.Xxs};
    top: 0.375rem;
    transition: opacity 120ms ease-in-out;
  }

  &:hover::after {
    opacity: 1;
  }

  &:active,
  .rbc-addons-dnd-dragged-event & {
    cursor: grabbing;
  }

  &:active::after,
  .rbc-addons-dnd-dragged-event &::after {
    opacity: 1;
  }
`;

export const DragHandleRevealZone = styled.div`
  height: 2rem;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: var(--z-index-raised1);
`;

export const TypePill = styled.div`
  align-items: center;
  background-color: var(--appointment-accent, ${Colors.gray['100']});
  border: 1px solid var(--appointment-accent, ${Colors.gray['100']});
  border-radius: var(--border-radius-full);
  color: ${Colors.white};
  display: inline-flex;
  gap: ${Spacing['3Xs']};
  max-width: 100%;
  min-width: 0;
  padding: 0 ${Spacing.Xxs};

  svg {
    color: ${Colors.white};
  }
`;

export const TypePillLabel = styled(Text)`
  color: ${Colors.white};
`;

export const AppointmentLink = styled(Link)`
  align-items: center;
  color: ${Colors.blue['100']};
  display: inline-flex;
  gap: ${Spacing['3Xs']};
  min-width: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

export const AppointmentFooter = styled.div`
  align-items: flex-end;
  display: flex;
  gap: ${Spacing.Xxs};
  justify-content: space-between;
`;

export const EditActionWrapper = styled.div`
  margin-left: auto;
`;

export const FilterWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  padding: var(--spacing-s);

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
    gap: ${Spacing.Xs};
  }
`;
export const Controls = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.Xxs};

  > span {
    padding: 0 ${Spacing.M};
  }

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing['3Xs']};

  > div {
    width: inherit;

    &:nth-of-type(1) {
      margin-bottom: ${Spacing['3Xs']};
    }
  }
`;

export const AppointmentButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing['3Xs']};
  margin-left: auto;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: space-between;
    margin-left: 0;
    width: 100%;
  }
`;

export const ShowText = styled(Text)`
  cursor: pointer;
`;
export const ShowItem = styled.div`
  cursor: pointer;
`;
export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing['3Xs']};
  max-height: 400px;
  overflow-y: auto;
`;

export const StatusDot = styled.div<{ $isBinding: boolean }>`
  background-color: ${({ $isBinding }) => ($isBinding ? Colors.green['100'] : 'transparent')};
  border: 2px solid ${Colors.green['100']};
  border-radius: 50%;
  height: var(--spacing-xxs);
  width: var(--spacing-xxs);
`;

export const CalendarStylesOverride = styled.div<{ $hideDragPreview?: boolean }>`
  .rbc-date-cell:hover .calendar-cell-new-btn,
  .rbc-date-cell:focus-within .calendar-cell-new-btn,
  .calendar-cell-new-btn.is-visible,
  .calendar-cell-new-btn:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }

  .rbc-event,
  .rbc-event-content,
  .rbc-event-label {
    cursor: default;
  }

  .rbc-event a,
  .rbc-event button {
    cursor: pointer;
  }

  .rbc-addons-dnd-resizable {
    overflow: visible;
    position: relative;
  }

  .rbc-addons-dnd-resize-ew-anchor {
    align-items: center;
    background-color: transparent;
    border-radius: 0;
    cursor: ew-resize;
    display: flex;
    height: 100% !important;
    justify-content: center;
    opacity: 1;
    overflow: visible;
    top: 0 !important;
    width: 0 !important;
    z-index: var(--z-index-raised1);

    &::before {
      background-color: transparent;
      content: '';
      height: 100%;
      position: absolute;
      top: 0;
      width: 1rem;
    }

    &::after {
      background-color: ${Colors.blue['100']};
      border-radius: var(--border-radius-xs);
      content: '';
      height: 100%;
      opacity: 0;
      position: absolute;
      top: 0;
      transition: opacity 120ms ease-in-out;
      width: 0.25rem;
    }

    &:hover::after {
      opacity: 0.95;
    }
  }

  .rbc-addons-dnd-resize-ew-anchor:first-child::before {
    left: 0;
    transform: translateX(0);
  }

  .rbc-addons-dnd-resize-ew-anchor:first-child::after {
    left: 0;
    transform: translateX(0);
  }

  .rbc-addons-dnd-resize-ew-anchor:last-child::before {
    right: 0;
    transform: translateX(0);
  }

  .rbc-addons-dnd-resize-ew-anchor:last-child::after {
    right: 0;
    transform: translateX(0);
  }

  .rbc-addons-dnd-resize-ew-icon {
    border: 0;
    height: ${Spacing.S};
    margin: 0;
    position: relative;
    width: ${Spacing.Xxs};
  }

  .rbc-addons-dnd-resize-ew-icon::before,
  .rbc-addons-dnd-resize-ew-icon::after {
    background-color: white;
    content: '';
    position: absolute;
    top: 0;
    width: 1px;
  }

  .rbc-addons-dnd-resize-ew-icon::before {
    bottom: 0;
    left: 0;
  }

  .rbc-addons-dnd-resize-ew-icon::after {
    bottom: 0;
    right: 0;
  }

  .rbc-addons-dnd-dragged-event {
    opacity: 1 !important;
  }

  ${({ $hideDragPreview }) =>
    $hideDragPreview &&
    css`
      .rbc-addons-dnd-drag-row {
        display: none;
      }

      .rbc-addons-dnd-over {
        background-color: transparent;
      }
    `}

  .rbc-month-view {
    position: relative;
    z-index: var(--z-index-base);
  }

  .rbc-day-bg.calendar-day-weekend,
  .rbc-day-bg.calendar-day-outside-month {
    background-color: ${Colors.gray['10']};
  }

  .rbc-month-row .rbc-row-content {
    position: relative;
    z-index: var(--z-index-raised1);
  }
`;

export const EventRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing['3Xs']};
`;

export const CalendarDateHeaderContainer = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3-xs);
  min-height: 2.75rem;
  width: 100%;
`;

export const CalendarDateHeaderLabel = styled(Text)`
  align-self: flex-end;
`;

export const CalendarCellNewButton = styled.button<{ $isVisible?: boolean }>`
  align-items: center;
  background-color: var(--colors-blue-3);
  border: 1px solid var(--colors-blue-20);
  border-left: 3px solid var(--colors-blue-100);
  border-radius: 0;
  box-sizing: border-box;
  color: var(--colors-blue-100);
  cursor: pointer;
  display: flex;
  font-size: 0.75rem;
  font-weight: 600;
  height: 2rem;
  justify-content: center;
  line-height: 1;
  margin: 0 0 var(--spacing-xs) 0;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  padding: var(--spacing-xs) var(--spacing-xxs);
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 120ms ease-in-out;
  white-space: nowrap;
  width: 100%;
  z-index: var(--z-index-raised1);

  &:hover {
    background-color: var(--colors-blue-5);
  }

  @media (max-width: 768px) {
    opacity: 1 !important;
    pointer-events: auto !important;
    height: 2.5rem;
    font-size: 0.875rem;
    padding: var(--spacing-s) var(--spacing-xs);
  }
`;

export const ScrollableCalendarContainer = styled.div`
  display: flex;

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
  width: 100%;

  & > div {
    min-width: 100%;
    scroll-snap-align: start;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
`;
export const MonthContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Xs};
`;

export const MonthsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.L};
`;

export const DayAgendaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  width: 100%;
`;

export const DayAgendaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs);
  border: 1px solid var(--colors-blue-20);
  border-radius: var(--border-radius-s);
  background-color: var(--colors-white);
  transition: all 0.2s ease;

  &[data-drag-over='true'] {
    background-color: var(--colors-blue-5);
    border-color: var(--colors-blue-100);
    border-width: 2px;
    box-shadow: 0 0 0 1px var(--colors-blue-100);
  }
`;

export const DayAgendaSectionTitle = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-bottom: var(--spacing-xs);
  border-bottom: 1px solid var(--colors-blue-20);
  margin-bottom: var(--spacing-xs);
`;

export const EmptyDayMessage = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
  justify-content: center;
  padding: var(--spacing-m) var(--spacing-xs);
  text-align: center;
  color: var(--colors-gray-100);
  width: 100%;

  .calendar-cell-new-btn {
    opacity: 1;
    pointer-events: auto;
    z-index: 2;
  }
`;
